/**
 * CBE Birr Plus Mini App payment helpers.
 *
 * Backend owns token validation, hashing, POST /auth/pay, and the callback.
 * Frontend: call createChechePayment, then hand the processed payment token
 * to the native popup via window.myJsChannel.postMessage.
 */

import {
  getCbeBirrPlusWindow,
  type CbeBirrPlusCreatePaymentRequest,
  type CbeBirrPlusCreatePaymentResult,
  type CbeBirrPlusPaymentResult,
} from "@/types/cbe-birr-plus"
import { CBE_BIRR_PLUS_PAYMENT_METHOD_ID, CBE_CREATE_PAYMENT_URL, PAY_API_URL } from "@/lib/cbe-config"
import { cbeDebugError, cbeDebugLog, maskSecret } from "@/lib/cbe-debug"
import Cookies from "js-cookie"

export { CBE_BIRR_PLUS_PAYMENT_METHOD_ID }

export const CBE_TOKEN_STORAGE_KEY = "cbe_birr_plus_token"

const LOG = "[CBE Birr Plus]"

function log(...args: unknown[]): void {
  console.log(LOG, ...args)
}

function logWarn(...args: unknown[]): void {
  console.warn(LOG, ...args)
}

function logError(...args: unknown[]): void {
  console.error(LOG, ...args)
}

function safeKeys(value: unknown): string[] {
  if (!value || (typeof value !== "object" && typeof value !== "function")) return []
  try {
    return Object.keys(value as object)
  } catch {
    return []
  }
}

function dumpWindowBridge(): Record<string, unknown> {
  if (typeof window === "undefined") return { window: "undefined" }
  const w = window as any
  const interesting = Object.keys(window).filter((key) =>
    /token|auth|cbe|birr|flutter|jsChannel|myJs|webkit|consumer|superapp/i.test(key)
  )
  return {
    href: window.location.href,
    origin: window.location.origin,
    search: window.location.search,
    hash: window.location.hash,
    userAgent: navigator.userAgent,
    myJsChannelType: typeof w.myJsChannel,
    myJsChannelKeys: safeKeys(w.myJsChannel),
    myJsChannelPostMessageType: typeof w.myJsChannel?.postMessage,
    cbesuperappType: typeof w.cbesuperapp,
    consumerappType: typeof w.consumerapp,
    interestingWindowKeys: interesting,
    cookie: document.cookie,
    localStorageKeys: (() => {
      try {
        return Object.keys(localStorage)
      } catch {
        return ["(inaccessible)"]
      }
    })(),
    sessionStorageKeys: (() => {
      try {
        return Object.keys(sessionStorage)
      } catch {
        return ["(inaccessible)"]
      }
    })(),
  }
}

let lastAvailability: boolean | null = null

export function isCbeBirrPlusAvailable(): boolean {
  if (typeof window === "undefined") return false
  const channel = getCbeBirrPlusWindow().myJsChannel
  const available = typeof channel?.postMessage === "function"
  if (lastAvailability !== available) {
    lastAvailability = available
    log("isCbeBirrPlusAvailable changed", {
      available,
      myJsChannelType: typeof channel,
      postMessageType: typeof channel?.postMessage,
      keys: safeKeys(channel),
    })
  }
  return available
}

function stripBearer(value: string | null | undefined): string | null {
  if (!value) return null
  const token = value.replace(/^Bearer\s+/i, "").trim()
  if (!token || token === "undefined" || token === "null") return null
  return token
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null
  log("readCookie raw document.cookie", document.cookie)
  const parts = document.cookie.split("; ")
  for (const part of parts) {
    const eq = part.indexOf("=")
    if (eq < 0) continue
    const key = decodeURIComponent(part.slice(0, eq))
    if (key !== name) continue
    try {
      const value = decodeURIComponent(part.slice(eq + 1))
      log("readCookie HIT", { name, rawLength: value.length })
      log("CBE TOKEN from cookie (full):", value)
      return value
    } catch {
      const value = part.slice(eq + 1)
      log("readCookie HIT (undecoded)", { name })
      log("CBE TOKEN from cookie undecoded (full):", value)
      return value
    }
  }
  log("readCookie MISS", { name, cookieNames: parts.map((p) => p.split("=")[0]) })
  return null
}

function persistToken(token: string): void {
  log("persistToken", { length: token.length })
  log("CBE TOKEN persist (full):", token)
  try {
    localStorage.setItem(CBE_TOKEN_STORAGE_KEY, token)
    log("persistToken localStorage OK")
  } catch (err) {
    logError("persistToken localStorage failed", err)
  }
  try {
    sessionStorage.setItem(CBE_TOKEN_STORAGE_KEY, token)
    log("persistToken sessionStorage OK")
  } catch (err) {
    logError("persistToken sessionStorage failed", err)
  }
  try {
    document.cookie = `${CBE_TOKEN_STORAGE_KEY}=${encodeURIComponent(token)}; Path=/; Max-Age=3600; SameSite=Lax`
    log("persistToken cookie OK", document.cookie)
  } catch (err) {
    logError("persistToken cookie failed", err)
  }
}

function tokenFromSearchParams(params: URLSearchParams, allowGenericToken: boolean, source: string): string | null {
  const candidates = {
    cbe_token: params.get("cbe_token"),
    cbeToken: params.get("cbeToken"),
    access_token: params.get("access_token"),
    accessToken: params.get("accessToken"),
    token: allowGenericToken ? params.get("token") : null,
  }
  log(`tokenFromSearchParams (${source})`, {
    allowGenericToken,
    keys: [...params.keys()],
    entries: candidates,
  })
  return stripBearer(
    candidates.cbe_token ||
      candidates.cbeToken ||
      candidates.access_token ||
      candidates.accessToken ||
      candidates.token
  )
}

export function captureCbeBirrPlusToken(): string | null {
  log("captureCbeBirrPlusToken START")
  if (typeof window === "undefined") {
    log("captureCbeBirrPlusToken aborted — no window")
    return null
  }

  log("captureCbeBirrPlusToken environment", dumpWindowBridge())

  const w = window as Window & { __CBE_BIRR_TOKEN__?: string }
  const allowGenericToken = typeof getCbeBirrPlusWindow().myJsChannel?.postMessage === "function"
  const params = new URLSearchParams(window.location.search)
  const hash = new URLSearchParams(String(window.location.hash || "").replace(/^#/, ""))

  const fromWindow = stripBearer(w.__CBE_BIRR_TOKEN__)
  const fromQuery = tokenFromSearchParams(params, allowGenericToken, "search")
  const fromHash = tokenFromSearchParams(hash, allowGenericToken, "hash")
  const fromCookie = stripBearer(readCookie(CBE_TOKEN_STORAGE_KEY))
  let fromLocal: string | null = null
  let fromSession: string | null = null
  try {
    fromLocal = stripBearer(localStorage.getItem(CBE_TOKEN_STORAGE_KEY))
  } catch (err) {
    logError("localStorage.getItem failed", err)
  }
  try {
    fromSession = stripBearer(sessionStorage.getItem(CBE_TOKEN_STORAGE_KEY))
  } catch (err) {
    logError("sessionStorage.getItem failed", err)
  }

  log("captureCbeBirrPlusToken sources", {
    fromWindow,
    fromQuery,
    fromHash,
    fromCookie,
    fromLocal,
    fromSession,
    allowGenericToken,
  })

  const found = fromWindow || fromQuery || fromHash || fromCookie || fromLocal || fromSession
  const source = fromWindow
    ? "window.__CBE_BIRR_TOKEN__"
    : fromQuery
      ? "url.search"
      : fromHash
        ? "url.hash"
        : fromCookie
          ? "cookie"
          : fromLocal
            ? "localStorage"
            : fromSession
              ? "sessionStorage"
              : "NONE"

  if (found) {
    log("captureCbeBirrPlusToken FOUND", { source, length: found.length })
    log("CBE TOKEN received (full):", found)
    persistToken(found)
  } else {
    logWarn("captureCbeBirrPlusToken NOT FOUND in any source")
  }

  return found
}

function extractProcessedToken(data: unknown): string | null {
  log("extractProcessedToken input type", typeof data, data)
  if (!data) return null
  if (typeof data === "string") {
    const trimmed = data.trim()
    if (!trimmed) return null
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        log("extractProcessedToken parsing JSON string")
        return extractProcessedToken(JSON.parse(trimmed))
      } catch (err) {
        logWarn("extractProcessedToken JSON parse failed, using raw string", err)
        return trimmed
      }
    }
    log("extractProcessedToken using raw string", { length: trimmed.length, preview: maskSecret(trimmed) })
    return trimmed
  }
  if (typeof data !== "object") return null

  const obj = data as Record<string, unknown>
  log("extractProcessedToken object keys", Object.keys(obj))
  const nested =
    obj.token ??
    (obj.result as Record<string, unknown> | undefined)?.token ??
    (obj.data as Record<string, unknown> | undefined)?.token ??
    (obj.chechePayresult as Record<string, unknown> | undefined)?.token

  if (typeof nested === "string" && nested.trim()) {
    log("extractProcessedToken nested token", { preview: maskSecret(nested), length: nested.trim().length })
    return nested.trim()
  }
  logWarn("extractProcessedToken no token field", obj)
  return null
}

/**
 * Ask the backend to hash + submit the CBE Birr Plus pay payload.
 * Returns the processed payment token CBE expects on the JS channel.
 */
export async function createCbeBirrPlusPayment(
  body: CbeBirrPlusCreatePaymentRequest
): Promise<CbeBirrPlusCreatePaymentResult> {
  log("createCbeBirrPlusPayment START", body)
  const apiBase =
    import.meta.env.VITE_API_URL ||
    import.meta.env.NEXT_PUBLIC_API_URL ||
    PAY_API_URL
  const url = apiBase ? `${apiBase.replace(/\/$/, "")}/v1/payments/createChechePayment` : CBE_CREATE_PAYMENT_URL
  log("createCbeBirrPlusPayment urls", {
    VITE_API_URL: import.meta.env.VITE_API_URL,
    NEXT_PUBLIC_API_URL: import.meta.env.NEXT_PUBLIC_API_URL,
    PAY_API_URL,
    CBE_CREATE_PAYMENT_URL,
    apiBase,
    url,
    paymentMethod: CBE_BIRR_PLUS_PAYMENT_METHOD_ID,
  })

  const cbeSessionToken = body.access_token || captureCbeBirrPlusToken() || undefined
  const qeteroUserToken = Cookies.get("qetero.token") || undefined

  const payload = {
    appointment_id: body.transaction_id,
    type: "service Fee",
    amount: typeof body.amount === "number" ? String(body.amount) : body.amount,
    title: body.title,
    paymentType: "InApp",
    paymentMethod: body.paymentMethod || CBE_BIRR_PLUS_PAYMENT_METHOD_ID,
    ...(cbeSessionToken ? { access_token: cbeSessionToken, cbe_token: cbeSessionToken } : {}),
  }

  // Qetero API validates Authorization as the logged-in user JWT.
  // The CBE Birr Plus session token must NOT go in Authorization — that caused 401.
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(qeteroUserToken ? { Authorization: `Bearer ${qeteroUserToken}` } : {}),
  }

  log("createChechePayment tokens", {
    hasCbeSessionToken: Boolean(cbeSessionToken),
    hasQeteroUserToken: Boolean(qeteroUserToken),
    qeteroUserToken: qeteroUserToken ? maskSecret(qeteroUserToken) : null,
    authorizationUses: qeteroUserToken ? "qetero.token" : "(none)",
    bodySendsCbeTokenAs: cbeSessionToken ? "access_token + cbe_token" : "(none)",
  })
  log("CBE TOKEN sent to backend (full):", cbeSessionToken || null)

  log("createChechePayment request", {
    url,
    method: "POST",
    headerKeys: Object.keys(headers),
    hasAuthorizationHeader: Boolean(headers.Authorization),
    payloadKeys: Object.keys(payload),
    appointment_id: payload.appointment_id,
    amount: payload.amount,
    title: payload.title,
    paymentType: payload.paymentType,
    paymentMethod: payload.paymentMethod,
    hasAccessToken: Boolean(cbeSessionToken),
    access_token: cbeSessionToken || null,
  })

  let res: Response
  try {
    log("createChechePayment fetch() sending…")
    res = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    })
    log("createChechePayment fetch() returned", {
      status: res.status,
      statusText: res.statusText,
      ok: res.ok,
      url: res.url,
      responseHeaders: Object.fromEntries(res.headers.entries()),
    })
  } catch (networkErr) {
    logError("createChechePayment network error", networkErr)
    cbeDebugError("CBE Birr Plus createChechePayment network error", networkErr)
    throw networkErr
  }

  const rawText = await res.text().catch((err) => {
    logError("createChechePayment failed to read body", err)
    return ""
  })
  log("createChechePayment HTTP body", {
    status: res.status,
    statusText: res.statusText,
    ok: res.ok,
    bodyLength: rawText.length,
    bodyPreview: rawText.slice(0, 2000),
  })
  cbeDebugLog("CBE Birr Plus createChechePayment HTTP", {
    status: res.status,
    statusText: res.statusText,
    ok: res.ok,
    bodyPreview: rawText.slice(0, 2000),
  })

  if (!res.ok) {
    logError("createChechePayment not OK", { status: res.status, body: rawText.slice(0, 2000) })
    if (res.status === 401 || res.status === 403) {
      throw new Error(`CBE_BIRR_UNAUTHORIZED: ${res.status} ${res.statusText}${rawText ? ` - ${rawText}` : ""}`)
    }
    throw new Error(`CBE Birr Plus createChechePayment failed: ${res.status} ${res.statusText}${rawText ? ` - ${rawText}` : ""}`)
  }

  let parsed: unknown = rawText
  try {
    parsed = JSON.parse(rawText)
    log("createChechePayment parsed JSON", parsed)
  } catch (err) {
    logWarn("createChechePayment response is not JSON, using raw text", err)
  }

  const token = extractProcessedToken(parsed)
  if (!token) {
    logError("createChechePayment missing processed token", parsed)
    cbeDebugError("CBE Birr Plus createChechePayment missing processed token", parsed)
    throw new Error("CBE_BIRR_MISSING_PAYMENT_TOKEN")
  }

  log("createChechePayment SUCCESS processed token length", token.length)
  log("CBE processed payment token received (full):", token)
  return { token, ...(typeof parsed === "object" && parsed ? (parsed as object) : {}) }
}

/**
 * Hand the processed payment token to CBE Birr Plus so the native pay popup opens.
 */
export function postCbeBirrPlusPaymentToken(token: string): void {
  log("postCbeBirrPlusPaymentToken START", { length: token.length })
  log("CBE processed payment token posting to myJsChannel (full):", token)
  const w = getCbeBirrPlusWindow()
  log("postCbeBirrPlusPaymentToken bridge", {
    myJsChannel: w.myJsChannel,
    type: typeof w.myJsChannel,
    postMessageType: typeof w.myJsChannel?.postMessage,
    keys: safeKeys(w.myJsChannel),
  })

  if (!isCbeBirrPlusAvailable()) {
    logError("postCbeBirrPlusPaymentToken failed — window.myJsChannel missing")
    cbeDebugError("postCbeBirrPlusPaymentToken failed — window.myJsChannel missing")
    throw new Error("CBE_BIRR_PLUS_UNAVAILABLE")
  }

  // CBE returns {"token": ProcessedPaymentToken}. The JS channel expects that JWT string.
  log("myJsChannel.postMessage ABOUT TO SEND (full JWT):", token)
  cbeDebugLog("myJsChannel.postMessage", {
    rawLength: token.length,
  })
  try {
    w.myJsChannel!.postMessage(token)
    log("myJsChannel.postMessage SENT OK")
  } catch (err) {
    logError("myJsChannel.postMessage THREW", err)
    throw err
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function isPaidAppointment(data: unknown): boolean {
  if (!data || typeof data !== "object") {
    log("isPaidAppointment false — not an object", data)
    return false
  }
  const obj = data as Record<string, unknown>
  const nested = (obj.data as Record<string, unknown> | undefined) || obj
  const paymentStatus = String(nested.payment_status ?? nested.paymentStatus ?? "").toLowerCase()
  const status = String(nested.status ?? "").toLowerCase()
  const txnRef = nested.txnRef ?? nested.txn_ref ?? nested.ft_number ?? nested.ftNumber
  const paidAmount = nested.paidAmount ?? nested.paid_amount
  const paid =
    paymentStatus === "paid" ||
    paymentStatus === "success" ||
    paymentStatus === "completed" ||
    (typeof txnRef === "string" && Boolean(txnRef.trim())) ||
    (paidAmount != null && Number(paidAmount) > 0) ||
    status === "confirmed" ||
    status === "paid" ||
    status === "success"

  log("isPaidAppointment", { paid, paymentStatus, status, txnRef, paidAmount, keys: Object.keys(nested) })
  return paid
}

async function waitForCbeBirrPlusCallback(
  appointmentId: string,
  timeoutMs: number
): Promise<CbeBirrPlusPaymentResult> {
  const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || ""
  const started = Date.now()
  const intervalMs = 2000
  let attempt = 0
  log("waitForCbeBirrPlusCallback START", { appointmentId, apiUrl, timeoutMs, intervalMs })

  while (Date.now() - started < timeoutMs) {
    attempt += 1
    const elapsed = Date.now() - started
    const pollUrl = `${apiUrl}/v1/payments/appointment/${appointmentId}`
    log(`poll attempt #${attempt}`, { elapsed, pollUrl })
    try {
      const res = await fetch(pollUrl, {
        headers: { "Content-Type": "application/json" },
      })
      log(`poll attempt #${attempt} HTTP`, { status: res.status, ok: res.ok })
      if (res.ok) {
        const data = await res.json()
        log(`poll attempt #${attempt} body`, data)
        if (isPaidAppointment(data)) {
          log("waitForCbeBirrPlusCallback CONFIRMED PAID", data)
          cbeDebugLog("CBE Birr Plus callback confirmed", data)
          return data as CbeBirrPlusPaymentResult
        }
        log(`poll attempt #${attempt} not paid yet`)
      } else {
        const text = await res.text().catch(() => "")
        logWarn(`poll attempt #${attempt} not OK`, { status: res.status, text: text.slice(0, 500) })
      }
    } catch (err) {
      logError(`poll attempt #${attempt} error`, err)
      cbeDebugError("CBE Birr Plus status poll error", err)
    }
    await sleep(intervalMs)
  }

  logError("waitForCbeBirrPlusCallback TIMEOUT", { appointmentId, attempts: attempt, timeoutMs })
  throw new Error("CBE_BIRR_PAYMENT_TIMEOUT")
}

/**
 * Full CBE Birr Plus pay flow:
 * createChechePayment (backend hashes + /auth/pay) → myJsChannel.postMessage → native popup
 * Then poll our API until the CBE callback marks the appointment paid.
 */
export async function payWithCbeBirrPlus(options: {
  transactionId: string
  amount: number
  title: string
  timeoutMs?: number
}): Promise<CbeBirrPlusPaymentResult> {
  const timeoutMs = options.timeoutMs ?? 120_000
  log("payWithCbeBirrPlus START", options)
  log("payWithCbeBirrPlus environment", dumpWindowBridge())

  const tokenNow = captureCbeBirrPlusToken()
  log("payWithCbeBirrPlus token before pay", {
    hasAccessToken: Boolean(tokenNow),
    myJsChannel: isCbeBirrPlusAvailable(),
  })
  log("CBE TOKEN before pay (full):", tokenNow || null)
  cbeDebugLog("payWithCbeBirrPlus start", {
    transactionId: options.transactionId,
    amount: options.amount,
    title: options.title,
    myJsChannel: isCbeBirrPlusAvailable(),
    hasAccessToken: Boolean(tokenNow),
  })

  if (!isCbeBirrPlusAvailable()) {
    logError("payWithCbeBirrPlus aborted — CBE_BIRR_PLUS_UNAVAILABLE")
    throw new Error("CBE_BIRR_PLUS_UNAVAILABLE")
  }

  const created = await createCbeBirrPlusPayment({
    transaction_id: options.transactionId,
    title: options.title,
    amount: options.amount,
  })
  log("payWithCbeBirrPlus createChechePayment done", {
    token: maskSecret(created.token),
    resultKeys: Object.keys(created),
  })

  postCbeBirrPlusPaymentToken(created.token)
  log("payWithCbeBirrPlus native popup should now be open — waiting for callback")

  try {
    const result = await waitForCbeBirrPlusCallback(options.transactionId, timeoutMs)
    log("payWithCbeBirrPlus SUCCESS", result)
    return result
  } catch (err) {
    logError("payWithCbeBirrPlus wait failed", err)
    cbeDebugError("payWithCbeBirrPlus wait failed", err)
    throw err
  }
}

export function isCbeBirrPlusPaymentSuccess(result: CbeBirrPlusPaymentResult | null | undefined): boolean {
  const ok = isPaidAppointment(result)
  log("isCbeBirrPlusPaymentSuccess", { ok, result })
  return ok
}
